import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { ArrowDownTrayIcon, DocumentTextIcon, PrinterIcon } from '@heroicons/react/24/outline';
import VideoHeaderStrip, { formatDuration } from '../components/VideoHeaderStrip';
import { findingsAPI } from '../services/findingsApi';
import { assessmentAPI, downloadFilename, saveBlob } from '../services/assessmentApi';
import { getApiErrorMessage } from '../utils/apiError';

/**
 * The Report tab (RUNLOG V8; the 2026-09-25 brief, section 7): GRIHA's own site-visit report,
 * filled in from the Review Queue, in the layout of GRIHA's Due Diligence report so an
 * auditor can compare the two like for like - one table per section, a green header row,
 * light-green criterion and appraisal rows, "(Mandatory)" and the criterion's type in red,
 * and the four columns Anshul chose: Criteria | Compliance on Site | Observations | Reference
 * Image. Rejected rows are left out; the report says DRAFT until every row is decided. The
 * same report downloads as Word or PDF (built on the server, app/services/griha_report.py).
 */

const HEADER_GREEN = 'bg-[#70AD47]';
const ROW_GREEN = 'bg-[#E2EFDA]';
const BORDER = 'border-[#A9D08E]';

/** A server time as a Date: a time with no zone is UTC (the server's), not local. */
function parseServerTime(value) {
  if (!value) return new Date(NaN);
  return new Date(/(?:[zZ]|[+-]\d\d:?\d\d)$/.test(value) ? value : `${value}Z`);
}

/** The server's reason for a failed download: with responseType 'blob' the JSON error body
 * arrives as a Blob, which getApiErrorMessage cannot read. */
async function readBlobError(error) {
  const body = error?.response?.data;
  if (typeof Blob === 'undefined' || !(body instanceof Blob)) return error;
  try {
    const text =
      typeof body.text === 'function'
        ? await body.text()
        : await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error);
            reader.readAsText(body);
          });
    const parsed = JSON.parse(text);
    return { ...error, response: { ...error.response, data: parsed } };
  } catch (parseError) {
    return error;
  }
}

function textBlocks(text) {
  return (text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) =>
      /^[-•*] /.test(line) ? { kind: 'bullet', text: line.slice(2).trim() } : { kind: 'para', text: line }
    );
}

function Blocks({ text }) {
  const blocks = textBlocks(text);
  const out = [];
  let bullets = [];
  const flush = () => {
    if (bullets.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-0.5">
          {bullets.map((b, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>{b}</li>
          ))}
        </ul>
      );
      bullets = [];
    }
  };
  blocks.forEach((block, i) => {
    if (block.kind === 'bullet') {
      bullets.push(block.text);
    } else {
      flush();
      // eslint-disable-next-line react/no-array-index-key
      out.push(<p key={`p-${i}`}>{block.text}</p>);
    }
  });
  flush();
  return <div className="space-y-1">{out}</div>;
}

function ReportRow({ row }) {
  return (
    <tr className="align-top break-inside-avoid" data-testid="report-row">
      <td className={`border ${BORDER} px-2 py-2`}>
        <p>{row.text}</p>
        {row.items?.length > 0 && (
          <ul className="list-disc pl-4 mt-1 italic">
            {row.items.map((item, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i}>
                {item.connector ? `${item.connector} ` : ''}
                {item.label ? `${item.label}: ` : ''}
                {item.text}
              </li>
            ))}
          </ul>
        )}
        {row.mandatory && <p className="text-red-700 mt-1">(Mandatory)</p>}
      </td>
      <td className={`border ${BORDER} px-2 py-2`}>{row.compliance_label}</td>
      <td className={`border ${BORDER} px-2 py-2`}>
        <Blocks text={row.observation} />
        {row.action && (
          <div className="mt-2">
            <p className="font-bold">Action to be taken:</p>
            <Blocks text={row.action} />
          </div>
        )}
      </td>
      <td className={`border ${BORDER} px-2 py-2`}>
        {row.images.map((image) => (
          <figure key={image.number} className="mb-2 last:mb-0">
            <img src={image.url} alt={image.caption || `Image ${image.number}`} className="w-full" />
            <figcaption className="text-[11px] italic mt-0.5">
              Image {image.number}: {image.caption}
            </figcaption>
          </figure>
        ))}
      </td>
    </tr>
  );
}

function ReportSection({ section }) {
  return (
    <section className="mb-8 break-inside-auto">
      <h2 className="text-base font-bold text-gray-900 mb-2">
        {section.numeral}. {section.title}
      </h2>
      <table className={`w-full border-collapse border ${BORDER} text-[13px] text-gray-900 bg-white`}>
        <colgroup>
          <col style={{ width: '24%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '37%' }} />
          <col style={{ width: '25%' }} />
        </colgroup>
        <thead>
          {/* GRIHA's own header green, with dark text: white on #70AD47 is 2.71:1, under
              WCAG AA (QA-46); gray-900 is 6.55:1. The Word and PDF keep DD1's white. */}
          <tr className={`${HEADER_GREEN} text-gray-900`}>
            {['Criteria', 'Compliance on Site', 'Observations', 'Reference Image'].map((label) => (
              <th key={label} className={`border ${BORDER} px-2 py-1.5 text-left font-bold`}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {section.criteria.map((criterion) => (
            <React.Fragment key={criterion.criterion_id}>
              <tr className={ROW_GREEN}>
                <td colSpan={4} className={`border ${BORDER} px-2 py-1.5`}>
                  <p className="font-bold">
                    Criterion {criterion.number}: {criterion.title}
                  </p>
                  {criterion.type_label && <p className="font-bold text-red-700">{criterion.type_label}</p>}
                </td>
              </tr>
              {criterion.appraisals.map((appraisal) => (
                <React.Fragment key={appraisal.ref}>
                  <tr className={ROW_GREEN}>
                    <td colSpan={4} className={`border ${BORDER} px-2 py-1.5 italic`}>
                      <span className="font-bold">Appraisal {appraisal.ref}:</span> {appraisal.text}
                    </td>
                  </tr>
                  {appraisal.rows.map((row, i) => (
                    <ReportRow key={row.entry_id || `${row.row_id}-${i}`} row={row} />
                  ))}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export default function ReportPage() {
  const { projectId, videoId } = useParams();
  const [downloading, setDownloading] = useState(null);

  const videoQuery = useQuery(['video', videoId], () => findingsAPI.getVideo(videoId), {
    enabled: !!videoId,
    select: (r) => r.data,
  });
  const { data: report, isLoading, isError, error, refetch } = useQuery(
    ['video-griha-report', videoId],
    () => assessmentAPI.report(videoId),
    { enabled: !!videoId, select: (r) => r.data, retry: false }
  );

  const download = async (kind) => {
    setDownloading(kind);
    try {
      const response = await assessmentAPI.download(videoId, kind);
      saveBlob(response.data, downloadFilename(response, `GRIHA-site-visit-report.${kind}`));
    } catch (err) {
      const readable = await readBlobError(err);
      toast.error(getApiErrorMessage(readable, `Could not download the ${kind === 'pdf' ? 'PDF' : 'Word'} report`));
    } finally {
      setDownloading(null);
    }
  };

  const headerStrip = (
    <div className="print:hidden">
      <VideoHeaderStrip
        video={videoQuery.data}
        isLoading={videoQuery.isLoading}
        isError={videoQuery.isError}
        error={videoQuery.error}
        projectId={projectId}
        videoId={videoId}
        activeTab="report"
      />
    </div>
  );

  if (isLoading) {
    return (
      <div className="p-6 va-report-page">
        {headerStrip}
        <div className="max-w-5xl mx-auto animate-pulse" data-testid="report-loading">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6" />
          <div className="h-64 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  if (isError || !report) {
    const is404 = error?.response?.status === 404;
    return (
      <div className="p-6 va-report-page">
        {headerStrip}
        <div className="max-w-4xl mx-auto text-center py-12 bg-white rounded-lg border border-gray-200">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {is404 ? 'No report yet' : 'Could not load this report'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {is404
              ? "The GRIHA report is written from a finished run and reviewed in the Review Queue."
              : getApiErrorMessage(error, 'Something went wrong loading this report.')}
          </p>
          <div className="mt-6 flex items-center justify-center gap-2">
            {is404 ? (
              <Link
                to={`/projects/${projectId}/videos/${videoId}/review`}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Go to the Review Queue
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => refetch()}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                Try again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const pending = report.counts?.pending || 0;
  const generated = parseServerTime(report.generated_at);

  return (
    <div className="p-6 va-report-page">
      {headerStrip}
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-end gap-2 mb-4 print:hidden flex-wrap">
          <button
            type="button"
            onClick={() => download('docx')}
            disabled={!!downloading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-[#F8F8FF] disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {downloading === 'docx' ? 'Preparing…' : 'Download Word'}
          </button>
          <button
            type="button"
            onClick={() => download('pdf')}
            disabled={!!downloading}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {downloading === 'pdf' ? 'Preparing…' : 'Download PDF'}
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-[#F8F8FF]"
          >
            <PrinterIcon className="h-4 w-4 mr-2" />
            Print
          </button>
        </div>

        <article className="bg-white border border-gray-200 rounded-lg p-8 print:border-0 print:p-0" data-testid="griha-report">
          <p className="text-xs text-gray-500 mb-4">{report.title}</p>
          <h1 className="text-2xl font-bold text-gray-900">GRIHA {report.title}</h1>
          <dl className="mt-2 text-sm text-gray-700 space-y-0.5">
            <div>
              <dt className="inline font-medium">Project: </dt>
              <dd className="inline">{report.project}</dd>
            </div>
            <div>
              {/* A photo set has no recording duration to report (Anshul, 2026-09-26); its
                  own label and its own photo count instead, from `report.video.photo_count`
                  (the backend's video block: {title, duration_s, kind, photo_count}). */}
              <dt className="inline font-medium">{report.video.kind === 'photos' ? 'Photo set: ' : 'Recording: '}</dt>
              <dd className="inline">
                {report.video.title}
                {report.video.kind === 'photos'
                  ? typeof report.video.photo_count === 'number'
                    ? ` (${report.video.photo_count} photo${report.video.photo_count === 1 ? '' : 's'})`
                    : ''
                  : report.video.duration_s
                  ? ` (${formatDuration(report.video.duration_s)})`
                  : ''}
              </dd>
            </div>
            <div>
              <dt className="inline font-medium">Standard: </dt>
              <dd className="inline">{report.standard}</dd>
            </div>
            <div>
              <dt className="inline font-medium">Generated: </dt>
              <dd className="inline">
                {Number.isNaN(generated.getTime())
                  ? report.generated_at
                  : generated.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
              </dd>
            </div>
          </dl>
          {report.draft && (
            <p className="mt-3 font-bold text-red-700" data-testid="report-draft">
              DRAFT:{' '}
              {pending > 0
                ? `${pending} row${pending === 1 ? '' : 's'} not yet reviewed by an auditor.`
                : report.counts?.rejected
                ? 'every row was rejected in the review; nothing is left to print.'
                : 'no row of the report has an answer yet.'}{' '}
              <Link
                to={`/projects/${projectId}/videos/${videoId}/review`}
                className="font-medium text-blue-700 underline print:hidden"
              >
                Go to the Review Queue
              </Link>
            </p>
          )}
          <div className="mt-8">
            {report.sections.length === 0 ? (
              <p className="text-sm text-gray-600" data-testid="report-empty">
                Nothing to print yet: answer or approve rows in the Review Queue and they appear here.
              </p>
            ) : (
              report.sections.map((section) => <ReportSection key={section.numeral} section={section} />)
            )}
          </div>
        </article>
      </div>
    </div>
  );
}
